import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

export const CONSENT_KEY = 'sd_cookie_consent';

/** Aktuelle Entscheidung lesen: 'accepted' | 'declined' | null */
export function getConsent() {
  try {
    const wert = localStorage.getItem(CONSENT_KEY);
    return wert === 'accepted' || wert === 'declined' ? wert : null;
  } catch {
    return null;
  }
}

/**
 * Cookie-Hinweis mit gleichwertiger Ablehnen-Option.
 * Messung und Statistik starten erst nach ausdrücklicher Zustimmung
 * (siehe HomePage: der Seitenaufruf wird vorher nicht gezählt).
 */
export default function CookieBanner() {
  const [sichtbar, setSichtbar] = useState(false);

  useEffect(() => {
    if (getConsent() === null) {
      const timer = setTimeout(() => setSichtbar(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const entscheiden = (wert) => {
    try {
      localStorage.setItem(CONSENT_KEY, wert);
    } catch {
      // Speicher nicht verfügbar (privater Modus): Auswahl gilt für diesen Besuch
    }
    // Andere Komponenten über die Entscheidung informieren
    window.dispatchEvent(new CustomEvent('sd-consent', { detail: wert }));
    setSichtbar(false);
  };

  return (
    <AnimatePresence>
      {sichtbar && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-x-0 bottom-0 z-[60] p-4 sm:p-6"
          role="dialog"
          aria-modal="false"
          aria-labelledby="cookie-titel"
          aria-describedby="cookie-text"
        >
          <div className="mx-auto max-w-3xl bg-ink text-white border-b-4 border-yellow shadow-2xl">
            <div className="p-6 sm:p-8">
              <h2
                id="cookie-titel"
                className="font-display text-xl sm:text-2xl text-yellow mb-3"
              >
                Cookies und Statistik
              </h2>

              <p id="cookie-text" className="text-white/90 text-sm sm:text-base leading-relaxed mb-2">
                Technisch notwendige Cookies brauchen wir, damit die Seite funktioniert.
                Zusätzlich möchten wir anonym zählen, wie oft welche Seite aufgerufen wird,
                um unsere Inhalte zu verbessern.
              </p>
              <p className="text-white/70 text-sm leading-relaxed mb-6">
                Die Zählung startet erst, wenn du zustimmst. Lehnst du ab, wird nichts
                gemessen. Du kannst deine Auswahl jederzeit ändern. Mehr dazu in der{' '}
                <Link
                  to="/privacy"
                  className="text-yellow underline underline-offset-2 hover:text-white transition-colors"
                >
                  Datenschutzerklärung
                </Link>.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => entscheiden('accepted')}
                  className="flex-1 px-6 py-3 bg-yellow text-ink font-display uppercase tracking-[0.08em] text-sm hover:bg-white transition-colors"
                >
                  Zustimmen
                </button>
                <button
                  onClick={() => entscheiden('declined')}
                  className="flex-1 px-6 py-3 bg-transparent text-white border-2 border-white/70 font-display uppercase tracking-[0.08em] text-sm hover:bg-white hover:text-ink transition-colors"
                >
                  Ablehnen
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
